/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";

import { Divider } from "@/components/custom/divider";
import { CustomIcon } from "@/components/custom/icons/custom-icon";
import OpenChatIcon from "@/components/custom/icons/open-chat";
import { StateSwitch } from "@/components/custom/state-switch";
import { Typography } from "@/components/custom/typography";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { USER_TYPE } from "@/lib/constants";
import { cn } from "@/lib/utils";
import { useMapStore } from "@/stores/mapStore";
import { useSearchConditionStore } from "@/stores/searchConditionStore";
import { useUser } from "@/stores/user";
import { useEffect, useState } from "react";
import SearchAIChat from "../../components/search-ai-chat";
import SearchCondition from "../../components/search-condition";
import LocationSelector from "../../components/search-condition/LocationSelector";
import { useTypedTranslations } from "@/i18n/typed";

export default function SearchTemplate() {
  const [tab, setTab] = useState("ai");
  const [open, setOpen] = useState(false);
  const [hasAutoOpened, setHasAutoOpened] = useState(false);
  const { userInfo } = useUser();
  const t = useTypedTranslations("searchTemplate");

  const { showHeatMap, showRadius, setShowHeatMap, setShowRadius } =
    useMapStore();

  const isMedia =
    userInfo?.user?.user_type?.toLocaleLowerCase() === USER_TYPE.media;

  const { isMyMedia, setIsMyMedia } = useSearchConditionStore();

  // Auto open sheet on first load for non-media users
  useEffect(() => {
    if (!isMedia && !hasAutoOpened) {
      setOpen(true);
      setHasAutoOpened(true);
    }
  }, [isMedia, hasAutoOpened]);

  const controlStyle = {
    transition: "right 0.3s ease",
    right: open ? "400px" : "4rem",
  };

  return (
    <div>
      <LocationSelector />

      {/* Controls */}
      {!isMedia && (
        <div
          className="fixed top-[var(--header-height)] z-30 mt-2.5"
          style={controlStyle}
        >
          <Popover>
            <PopoverTrigger asChild>
              <Button
                className="group shadow-xl bg-background rounded-md data-[state=open]:bg-primary flex-col px-[3px] !py-[4px] gap-0"
                variant="secondary"
                tone="light"
                prefix={
                  <CustomIcon
                    name="Map"
                    className="group-data-[state=open]:text-white text-gray-700"
                  />
                }
              >
                <Typography
                  variant="detail2"
                  className="group-data-[state=open]:text-white"
                >
                  {t("mapOptions.title")}
                </Typography>
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end">
              <div className="flex justify-between items-center mb-2">
                <Typography variant="detail1">
                  {t("mapOptions.radius")}
                </Typography>
                <StateSwitch
                  value={showRadius ? "on" : "off"}
                  onValueChange={() => setShowRadius(!showRadius)}
                />
              </div>
              <Divider />
              <div className="flex justify-between items-center my-2">
                <Typography variant="detail1">
                  {t("mapOptions.heatmap")}
                </Typography>
                <StateSwitch
                  value={showHeatMap ? "on" : "off"}
                  onValueChange={() => setShowHeatMap(!showHeatMap)}
                />
              </div>
              {showHeatMap && (
                <div className="mt-3 grid grid-cols-5 text-[10px] text-center">
                  {[
                    { key: "veryLow", color: "#14ADFF", value: "0~800" },
                    { key: "low", color: "#5BE924", value: "~1,600" },
                    { key: "medium", color: "#FFC800", value: "~2,400" },
                    { key: "high", color: "#FF9500", value: "~3,200" },
                    { key: "veryHigh", color: "#F15757", value: "3,201~" },
                  ].map((i, idx) => (
                    <div key={idx}>
                      <div
                        className="w-full h-[10px]"
                        style={{ backgroundColor: i.color }}
                      />
                      <div className="flex flex-col items-center mt-1">
                        <Typography variant="detail2">
                          {t(`mapOptions.levels.${i.key}` as any)}
                        </Typography>
                        <Typography variant="detail2">{i.value}</Typography>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </PopoverContent>
          </Popover>
        </div>
      )}

      {/* Sheet and Trigger */}
      {isMedia ? (
        <div className="fixed top-[var(--header-height)] right-2.5 z-40 flex space-x-1 shadow-xl bg-background rounded-md h-11 items-center mt-2.5 px-2.5">
          <label className="flex space-x-1 text-xs">
            <Typography variant="subheading3" className="font-medium">
              {t("myMediaOnly")}
            </Typography>
            <Switch
              onCheckedChange={(value) => setIsMyMedia(value)}
              checked={isMyMedia}
              id="mySetting"
            />
          </label>
        </div>
      ) : (
        <Sheet open={open} onOpenChange={(v) => setOpen(v)} modal={false}>
          <SheetTrigger asChild>
            <Button
              className="fixed top-[var(--header-height)] right-2.5 mt-2.5 z-40 bg-white flex shadow-xl flex-col px-[3px] !py-[4px] h-auto items-center justify-center gap-[1px] rounded-[2px] text-gray-700 hover:bg-gray-100"
              variant="secondary"
              tone={"light"}
              prefix={<OpenChatIcon className="text-gray-500" size={22} />}
            >
              <Typography variant="detail2">{t("openSearch")}</Typography>
            </Button>
          </SheetTrigger>

          <SheetContent
            onInteractOutside={(e) => e.preventDefault()}
            overlayVisible={false}
            className="
              z-50 shadow-lg flex flex-col border-0
              mt-[var(--header-height)]
              h-[calc(100vh-var(--header-height))]
              w-full sm:w-[395px]
              overflow-hidden
            "
          >
            <SheetHeader className="px-2.5 py-2 shadow-md/8 shrink-0">
              <SheetTitle>
                <div className="flex w-full items-center justify-between">
                  <div>
                    <Tabs
                      defaultValue="ai"
                      value={tab}
                      onValueChange={setTab}
                      className="w-auto"
                    >
                      <TabsList
                        variant="button"
                        className="flex p-[3px] space-x-2"
                      >
                        <TabsTrigger
                          asChild
                          value="ai"
                          className="data-[state=active]:text-white"
                        >
                          <Button
                            appearance={"outline"}
                            tone={"light"}
                            className="border-0 data-[state=active]:white text-gray-600 text-base"
                          >
                            {t("tabs.ai")}
                          </Button>
                        </TabsTrigger>
                        <TabsTrigger
                          asChild
                          value="condition"
                          className="data-[state=active]:text-white"
                        >
                          <Button
                            appearance={"outline"}
                            tone={"light"}
                            className="border-0 text-gray-600 text-base"
                          >
                            {t("tabs.condition")}
                          </Button>
                        </TabsTrigger>
                      </TabsList>
                    </Tabs>
                  </div>
                </div>
              </SheetTitle>
            </SheetHeader>

            <Tabs value={tab} className="flex-1 relative overflow-hidden">
              <TabsContent
                value="ai"
                className={cn(
                  "flex flex-col p-2.5 h-full overflow-auto",
                  tab !== "ai" && "hidden",
                )}
                forceMount
              >
                <SearchAIChat />
              </TabsContent>

              <TabsContent
                value="condition"
                className={cn(
                  "absolute top-0 left-0 right-0 bottom-0 overflow-auto",
                  tab !== "condition" && "hidden",
                )}
                forceMount
              >
                <SearchCondition />
              </TabsContent>
            </Tabs>
          </SheetContent>
        </Sheet>
      )}
    </div>
  );
}
